<template>
  <div :class="$style.timeDisplayContainer" @click.stop>
    <div
      v-if="isEditing"
      :class="[$style.timeEditContainer, { [$style.estimate]: isEstimate }]"
    >
      <input
        v-model="editingHours"
        type="number"
        min="0"
        :class="$style.timeInput"
        @keyup.enter="saveTime"
        @blur="handleBlur"
        ref="hoursInput"
      />
      <span>h</span>
      <input
        v-model="editingMinutes"
        type="number"
        min="0"
        max="59"
        :class="$style.timeInput"
        @keyup.enter="saveTime"
        @blur="handleBlur"
        ref="minutesInput"
      />
      <span>m</span>
    </div>
    <span
      v-else
      :class="[
        isEstimate ? $style.taskEstimate : $style.taskDuration,
        isEstimate && (props.actualDuration ?? 0) > duration
          ? $style.exceeded
          : '',
        isEstimate || props.allowEdit ? $style.editable : '',
      ]"
      @click="handleClick"
    >
      {{ label }} {{ formatDuration(duration) }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from "vue";

const props = defineProps<{
  duration: number;
  isActive: boolean;
  label?: string;
  isEstimate?: boolean;
  actualDuration?: number;
  allowEdit?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:duration", duration: number): void;
  (e: "click"): void;
}>();

const isEditing = ref(false);
const editingHours = ref(0);
const editingMinutes = ref(0);
const hoursInput = ref<HTMLInputElement | null>(null);
const minutesInput = ref<HTMLInputElement | null>(null);

function formatDuration(ms: number | null | undefined): string {
  console.log("TimeDisplay formatDuration called with:", ms);
  if (!ms) return "0h 0m";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((ms % (1000 * 60)) / 1000);

  let result = "";
  if (hours > 0) {
    result += `${hours}h`;
  }
  if (minutes > 0 || hours > 0) {
    result += `${minutes}m`;
  }
  if (seconds > 0 || (minutes === 0 && hours === 0)) {
    result += `${seconds}s`;
  }

  result = result.trim();
  console.log("TimeDisplay formatDuration result:", result);
  return result;
}

function handleBlur() {
  // Use setTimeout to check if focus moved to the other input
  setTimeout(() => {
    const activeElement = document.activeElement;
    if (
      activeElement !== hoursInput.value &&
      activeElement !== minutesInput.value
    ) {
      saveTime();
    }
  }, 10);
}

function startEditing() {
  if (props.isActive) return;

  isEditing.value = true;
  const hours = Math.floor(props.duration / (1000 * 60 * 60));
  const minutes = Math.floor((props.duration % (1000 * 60 * 60)) / (1000 * 60));
  editingHours.value = hours;
  editingMinutes.value = minutes;

  nextTick(() => {
    if (hoursInput.value) {
      hoursInput.value.focus();
      hoursInput.value.select();
    }
  });
}

function saveTime() {
  if (isEditing.value) {
    const hours = Math.max(0, editingHours.value || 0);
    const minutes = Math.min(59, Math.max(0, editingMinutes.value || 0));
    const newDuration = hours * 60 * 60 * 1000 + minutes * 60 * 1000;

    if (newDuration !== props.duration) {
      emit("update:duration", newDuration);
    }

    isEditing.value = false;
    editingHours.value = 0;
    editingMinutes.value = 0;
  }
}

function handleClick() {
  if (props.isEstimate || props.allowEdit) {
    startEditing();
  } else {
    emit("click");
  }
}
</script>

<style module>
.timeDisplayContainer {
  margin-left: auto;
  min-width: 0; /* Allow shrinking */
}

.timeEditContainer {
  display: flex;
  align-items: center;
  gap: 2px;
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 4px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
}

.timeEditContainer.estimate {
  background: rgba(255, 255, 255, 0.1);
  font-size: 0.75em;
}

.timeInput {
  width: 30px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 6px 8px;
  color: white;
  font-size: inherit;
  font-family: inherit;
  text-align: center;
}

.timeInput::-webkit-inner-spin-button,
.timeInput::-webkit-outer-spin-button {
  opacity: 0;
}

.timeInput:focus {
  outline: none;
  border-color: var(--primary-color);
  background: rgba(255, 255, 255, 0.15);
}

/* Ensure estimated time field has 16px font size to prevent iOS zoom */
.timeEditContainer.estimate .timeInput {
  font-size: 16px !important;
}

.timeEditContainer span {
  color: white;
  opacity: 0.8;
  font-size: 0.85em;
  user-select: none;
}

.taskEstimate {
  font-size: 16px !important;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
  padding: 1px 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.taskEstimate:hover {
  background: rgba(255, 255, 255, 0.2);
}

.taskDuration {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  font-size: 0.9em;
  white-space: nowrap;
}

.taskDuration.editable {
  cursor: pointer;
  transition: background-color 0.2s;
}

.taskDuration.editable:hover {
  background: rgba(255, 255, 255, 0.3);
}

.taskEstimate.exceeded {
  color: var(--task-design);
  font-weight: 500;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 768px) {
  .timeEditContainer {
    gap: 1px;
    padding: 1px 3px;
  }

  .timeInput {
    width: 25px;
    padding: 1px;
    font-size: 0.85em;
  }

  .timeEditContainer span {
    font-size: 0.8em;
  }

  .taskDuration {
    padding: 1px 4px;
    font-size: 0.85em;
  }

  .taskEstimate {
    font-size: 0.7em;
    padding: 1px 3px;
  }
}

@media (max-width: 480px) {
  .timeInput {
    width: 22px;
    font-size: 0.8em;
  }

  .taskDuration {
    font-size: 0.8em;
    padding: 1px 3px;
  }

  .taskEstimate {
    font-size: 0.65em;
    padding: 1px 2px;
  }
}
</style>
